import { itemsApi } from "#/api/itemsApi";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { Button, Modal, Select, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface BulkUpdateStatusModalProps {
  opened: boolean;
  onClose: () => void;
  selectedItemIds: number[];
  onSuccess: () => void;
}
const BulkUpdateStatusModal = ({
  opened,
  onClose,
  selectedItemIds,
  onSuccess,
}: BulkUpdateStatusModalProps) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({
      itemIds,
      status,
    }: {
      itemIds: number[];
      status: string;
    }) => {
      const response = await itemsApi.bulkUpdateStatus(itemIds, status);
      if (!response.data) {
        throw new Error("Failed to update items");
      }
      return response.data;
    },
    onSuccess: (data) => {
      notifications.show({
        title: t("common.success"),
        message: t("items.bulkUpdateStatus.success", { count: data.updated }),
        color: "green",
      });
      handleClose();
      onSuccess();
    },
    onError: (error: unknown) => {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || t("items.bulkUpdateStatus.error");
      notifications.show({
        title: t("common.error"),
        message: errorMessage,
        color: "red",
      });
    },
  });

  const handleClose = () => {
    setSelectedStatus(null);
    onClose();
  };

  const handleSave = () => {
    if (selectedStatus) {
      bulkUpdateMutation.mutate({
        itemIds: selectedItemIds,
        status: selectedStatus,
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={t("items.bulkUpdateStatus.title")}
      closeOnClickOutside={false}
      size="md"
      classNames={{
        title: "font-bold",
      }}
    >
      <div className="w-full flex flex-col gap-4">
        <Text size="sm" c="dimmed">
          {t("items.bulkUpdateStatus.description", { count: selectedItemIds.length })}
        </Text>

        <Select
          label={t("common.status")}
          placeholder={t("items.bulkUpdateStatus.selectNewStatus")}
          searchable
          clearable
          withAsterisk
          data={allowedStatuses.map((status) => ({
            value: status,
            label: sanitizeItemStatusLabel(status),
          }))}
          value={selectedStatus}
          onChange={setSelectedStatus}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            color="black"
            onClick={handleClose}
            disabled={bulkUpdateMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              disabled={
                !selectedStatus ||
                bulkUpdateMutation.isPending ||
                !canWrite()
              }
              onClick={handleSave}
              loading={bulkUpdateMutation.isPending}
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      </div>
    </Modal>
  );
};

export default BulkUpdateStatusModal;
